<template>
    <div class="sce">
        <div class="sce-left">
            <div class="sce-spacer"></div>
            <kimsa-combobox class="sce-sel" :placeholder="$t('common.select_placeholder')" dense :items="repsOptions"
                @change="repsType = $event" :value="repsType" placeholder-class ref="repsType" />
            <kimsa-combobox v-if="isStrongCategory" class="sce-sel" :placeholder="$t('common.select_placeholder')" dense
                :items="rpeOptions" @change="onChangeRpeType" :value="rpeType" placeholder-class
                :clearable="!!rpeType" @clear="rpeType = null" ref="rpeType" />
            <div v-if="isStrongCategory" class="sce-static" @click="onNoWeightOption">
                <span>{{ $t('exercises.weight') }}</span>
                <v-icon size="18">{{ disabledWeight ? 'mdi-check' : 'mdi-close' }}</v-icon>
            </div>
            <kimsa-combobox v-if="isStrongCategory" class="sce-sel" :placeholder="$t('common.select_placeholder')" dense
                :items="tempoOptions" @change="onChangeTempoType" :value="tempoType" placeholder-class
                :clearable="!!tempoType" @clear="tempoType = null" ref="tempoType" />
            <kimsa-combobox v-if="hasIntensity" class="sce-sel" :placeholder="$t('common.select_placeholder')" dense
                :items="intensityOptions" @change="onChangeIntensityType" :value="intensityType" placeholder-class
                ref="intensityType" />
            <div v-if="hasMicroPause" class="sce-static sce-static--plain">
                <span>{{ $t('exercises.micropause') }}</span>
            </div>
        </div>
        <div class="sce-scroll">
            <div class="sce-series">
                <div v-for="s in localSet" :key="s" class="sce-col">
                    <div class="sce-shead">S{{ s }}</div>
                    <v-text-field class="sce-inp" solo flat dense hide-details :readonly="!repsType"
                        v-model="dataSet.repsType[s - 1]" @keypress="onRepsKeypress"
                        @change="setSameValue(s - 1, dataSet.repsType[s - 1], 'repsType')" />
                    <v-text-field v-if="isStrongCategory" class="sce-inp" solo flat dense hide-details
                        :type="hasDecimals(rpeType) ? 'text' : 'number'"
                        :inputmode="hasDecimals(rpeType) ? 'decimal' : 'numeric'"
                        :step="hasDecimals(rpeType) ? undefined : stepRpe(rpeType)" :readonly="!rpeType"
                        v-model="dataSet.rpeType[s - 1]"
                        @keypress="onRpeKeypress" @blur="maxValue('rpeType', maxValueRpeType(), s - 1)"
                        @change="onRpeValueChange(s - 1)" />
                    <v-text-field v-if="isStrongCategory" class="sce-inp" solo flat dense hide-details
                        :readonly="dataSet.weight[s - 1] === '- s/p -'" v-model="dataSet.weight[s - 1]"
                        @change="setSameValue(s - 1, dataSet.weight[s - 1], 'weight')" />
                    <v-text-field v-if="isStrongCategory" class="sce-inp" solo flat dense hide-details
                        :maxlength="tempoType && tempoType.value === 'tempo' ? undefined : 3" :readonly="!tempoType"
                        v-model="dataSet.tempoType[s - 1]" @keypress="onTempoKeypress"
                        @change="setSameValue(s - 1, dataSet.tempoType[s - 1], 'tempoType')" />
                    <v-text-field v-if="hasIntensity" class="sce-inp" solo flat dense hide-details type="text"
                        :inputmode="hasDecimals(intensityType) ? 'decimal' : undefined"
                        v-model="dataSet.intensityType[s - 1]" @keypress="onIntensityKeypress"
                        @change="onIntensityValueChange(s - 1)" />
                    <v-text-field v-if="hasMicroPause" class="sce-inp" solo flat dense hide-details placeholder="00:00"
                        v-model="dataSet.micropause[s - 1]" @keypress="onMinuteSecondsKeypress"
                        @change="setSameValue(s - 1, dataSet.micropause[s - 1], 'micropause')" />
                </div>
                <div class="sce-col sce-add">
                    <div class="sce-shead"></div>
                    <button-tooltip :tooltip="$t('exercises.add_set')" top>
                        <template v-slot:activator="{ on, attrs }">
                            <button type="button" class="sce-add-btn" :disabled="localSet >= setMax" @click="addSeries"
                                v-bind="attrs" v-on="on">
                                <v-icon size="18">mdi-plus</v-icon>
                            </button>
                        </template>
                    </button-tooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import ButtonTooltip from "@/components/common/ButtonTooltip";
import {
    SET_NAMES, getRepsOptions, getRpeOptions, getTempoOptions, getIntensityOptions, findSetData, setOptionByShortName,
    validateRepsKey, validateRpeKey, validateTempoKey, validateIntensityKey, validateMinuteSecondsKey,
    cleanRpeValue, cleanIntensityValue, isDecimalSetType
} from "@/utils/Exercises";

const DATA_SET_DEFAULTS = { repsType: 0, rpeType: 0, weight: 0, tempoType: 0, intensityType: 0, micropause: '' }

export default {
    name: "SeriesConfigEditor",
    components: { KimsaCombobox, ButtonTooltip },
    props: {
        category: {
            type: Object,
            default: null,
        },
        set: {
            type: Number,
            default: 1,
        },
        maxWeight: {
            type: [Number, String],
            default: null,
        },
        setMax: {
            type: Number,
            default: 10,
        },
    },
    data(vm) {
        return {
            localSet: vm.set,
            dataSet: {},
            repsType: null,
            rpeType: null,
            tempoType: null,
            intensityType: null,
            disabledWeight: false,
            populating: false,
            rpeOptions: getRpeOptions(),
            tempoOptions: getTempoOptions(),
            intensityOptions: getIntensityOptions(),
        }
    },
    computed: {
        isStrongCategory(vm = this) {
            const key = vm.categoryKey(vm.category?.name)
            return key === vm.CATEGORY.STRONG || key === vm.CATEGORY.STABILITY || key === vm.CATEGORY.MOBILITY
        },
        hasMicroPause(vm = this) {
            const key = vm.categoryKey(vm.category?.name)
            return key !== vm.CATEGORY.STABILITY && key !== vm.CATEGORY.MOBILITY
        },
        hasIntensity(vm = this) {
            const key = vm.categoryKey(vm.category?.name)
            return key === vm.CATEGORY.SPEED || key === vm.CATEGORY.RESISTANCE
        },
        repsOptions(vm = this) {
            return getRepsOptions(vm.hasIntensity)
        },
        repsNames(vm = this) {
            return vm.hasIntensity ? SET_NAMES.REPS_INTENSITY : SET_NAMES.REPS
        },
    },
    created() {
        if (this.category) this.buildDefault()
    },
    methods: {
        buildDefault() {
            const vm = this
            vm.repsType = vm.repsOptions[0]
            vm.setDataSet(vm.localSet)
        },
        addQuantitySet(qty, value = null) {
            return Array.from({ length: qty }, () => value)
        },
        resizeDataSet(payload, qty) {
            const vm = this
            Object.keys(DATA_SET_DEFAULTS).forEach(key => {
                if (!Array.isArray(payload[key])) return
                if (payload[key].length > qty) payload[key] = payload[key].slice(0, qty)
                else if (payload[key].length < qty) {
                    const fill = payload[key].length ? payload[key][0] : DATA_SET_DEFAULTS[key]
                    payload[key].push(...vm.addQuantitySet(qty - payload[key].length, fill))
                }
            })
        },
        setDataSet(qty, oldQty = null) {
            const vm = this
            let payload = vm.dataSet

            if (oldQty) {
                vm.resizeDataSet(payload, qty)
            } else {
                if (!vm.category) return
                payload = {}
                payload.repsType = vm.addQuantitySet(qty, 0)
                if (vm.isStrongCategory) {
                    payload.rpeType = vm.addQuantitySet(qty, 0)
                    payload.weight = vm.addQuantitySet(qty, 0)
                    payload.tempoType = vm.addQuantitySet(qty, 0)
                }
                if (vm.hasIntensity) {
                    payload.intensityType = vm.addQuantitySet(qty, 0)
                }
                if (vm.hasMicroPause) {
                    payload.micropause = vm.addQuantitySet(qty, '')
                }
            }

            vm.dataSet = { ...payload }
        },
        setSameValue(index, value, attribute) {
            const vm = this
            if (index !== 0) return
            let dataSet = { ...vm.dataSet }
            dataSet[attribute] = vm.addQuantitySet(vm.localSet, value)
            vm.dataSet = dataSet
        },
        onNoWeightOption() {
            const vm = this
            if (vm.maxWeight) return
            vm.disabledWeight = !vm.disabledWeight
            let value = vm.disabledWeight ? '- s/p -' : ''
            vm.dataSet.weight.forEach((item, i) => {
                vm.setSameValue(i, value, 'weight')
            })
        },
        calculateWeight() {
            const vm = this
            let maxWeight = Number(vm.maxWeight || 0)
            if (vm.rpeType?.value === 'percentage' && maxWeight > 0)
                vm.dataSet.rpeType.forEach((percentage, i) => {
                    vm.dataSet.weight[i] = Math.round((maxWeight * percentage) / 100)
                })
        },
        hasDecimals(type) {
            return isDecimalSetType(type)
        },
        stepRpe(rpeType) {
            if (rpeType?.value === 'meter_seconds') return 0.1
            if (rpeType?.value === 'rpe') return 0.5
            return 1
        },
        onRepsKeypress(event) {
            validateRepsKey(event)
        },
        onRpeKeypress(event) {
            validateRpeKey(event, this.rpeType)
        },
        onTempoKeypress(event) {
            validateTempoKey(event, this.tempoType)
        },
        onIntensityKeypress(event) {
            validateIntensityKey(event, this.intensityType)
        },
        onMinuteSecondsKeypress(event) {
            validateMinuteSecondsKey(event)
        },
        onRpeValueChange(index) {
            const vm = this
            const value = cleanRpeValue(vm.dataSet.rpeType[index], vm.rpeType)
            vm.$set(vm.dataSet.rpeType, index, value)
            vm.setSameValue(index, value, 'rpeType')
        },
        onIntensityValueChange(index) {
            const vm = this
            const value = cleanIntensityValue(vm.dataSet.intensityType[index], vm.intensityType)
            vm.$set(vm.dataSet.intensityType, index, value)
            vm.setSameValue(index, value, 'intensityType')
        },
        maxValue(attribute, max, index) {
            const vm = this
            if (max === undefined) return
            if (vm.dataSet[attribute][index] > max) {
                let dataSet = { ...vm.dataSet }
                dataSet[attribute][index] = max
                vm.dataSet = dataSet
            }
        },
        maxValueRpeType() {
            const vm = this
            if (vm.rpeType?.value === 'percentage') return 100
            if (vm.rpeType?.value === 'rir' || vm.rpeType?.value === 'rpe') return 10
            return undefined
        },
        onChangeRpeType(type) {
            const vm = this
            vm.rpeType = type
            let dataSet = { ...vm.dataSet }
            dataSet.rpeType = vm.addQuantitySet(vm.localSet, 0)
            vm.dataSet = dataSet
        },
        onChangeTempoType(type) {
            const vm = this
            vm.tempoType = type
            let dataSet = { ...vm.dataSet }
            let value = type?.value === 'tempo' ? 0 : ''
            dataSet.tempoType = vm.addQuantitySet(vm.localSet, value)
            vm.dataSet = dataSet
        },
        onChangeIntensityType(type) {
            const vm = this
            vm.intensityType = type
            let dataSet = { ...vm.dataSet }
            let value = (type?.value === 'time' || type?.value === 'time_km') ? '00:00' : ''
            dataSet.intensityType = vm.addQuantitySet(vm.localSet, value)
            vm.dataSet = dataSet
        },
        addSeries() {
            const vm = this
            if (vm.localSet < vm.setMax) vm.localSet = vm.localSet + 1
        },
        generateConfig() {
            const vm = this
            let config = { sets: [] }
            const typesToAdd = [
                { prop: 'repsType', name: 'exercises.reps' },
                { prop: 'rpeType', name: 'exercises.rpe' },
                { prop: 'weight', name: 'exercises.weight', alwaysAdd: vm.isStrongCategory },
                { prop: 'tempoType', name: 'exercises.tempo' },
                { prop: 'intensityType', name: 'exercises.intensity' },
                { prop: 'micropause', name: 'exercises.micropause', alwaysAdd: true }
            ]
            for (let i = 0; i < vm.localSet; i++) {
                let data = []
                typesToAdd.forEach(({ prop, name, alwaysAdd }) => {
                    if ((alwaysAdd || vm[prop]) && vm.dataSet[prop]) {
                        data.push({
                            short_name: `short_${vm[prop]?.value || prop}`,
                            value: vm.dataSet[prop][i],
                        })
                    }
                })
                config.sets.push({ data })
            }
            return config
        },
        populate(config) {
            const vm = this
            const dataSet = vm.parseConfig(config)
            vm.populating = true
            vm.localSet = (config && config.sets && config.sets.length) || 1
            vm.$nextTick(() => {
                vm.dataSet = dataSet
                vm.populating = false
                vm.emitValid()
            })
        },
        parseConfig(config) {
            const vm = this
            let dataSet = {}
            if (config) {
                config.sets.forEach((set, i) => {
                    if (i === 0) {
                        let repsType = findSetData(set, vm.repsNames)
                        vm.repsType = setOptionByShortName(vm.repsOptions, repsType?.short_name)
                        if (repsType) dataSet.repsType = []
                        let rpeType = vm.isStrongCategory ? findSetData(set, SET_NAMES.RPE) : null
                        vm.rpeType = setOptionByShortName(vm.rpeOptions, rpeType?.short_name)
                        if (rpeType) dataSet.rpeType = []
                        let tempoType = findSetData(set, SET_NAMES.TEMPO)
                        vm.tempoType = setOptionByShortName(vm.tempoOptions, tempoType?.short_name)
                        if (tempoType) dataSet.tempoType = []
                        if (set.data.find(data => data.short_name === 'short_weight') || vm.isStrongCategory) dataSet.weight = []
                        let intensityType = vm.hasIntensity ? findSetData(set, SET_NAMES.INTENSITY) : null
                        vm.intensityType = setOptionByShortName(vm.intensityOptions, intensityType?.short_name)
                        if (intensityType) dataSet.intensityType = []
                        if (set.data.find(data => data.short_name === 'short_micropause')) dataSet.micropause = []
                    }

                    set.data.forEach((data) => {
                        if (vm.repsType && vm.repsNames.includes(data.short_name)) dataSet.repsType.push(data.value)
                        if (vm.rpeType && SET_NAMES.RPE.includes(data.short_name)) dataSet.rpeType.push(data.value)
                        if (vm.tempoType && SET_NAMES.TEMPO.includes(data.short_name)) dataSet.tempoType.push(data.value)
                        if (dataSet.weight && data.short_name === 'short_weight') dataSet.weight.push(data.value)
                        if (vm.intensityType && SET_NAMES.INTENSITY.includes(data.short_name)) dataSet.intensityType.push(data.value)
                        if (dataSet.micropause && data.short_name === 'short_micropause') dataSet.micropause.push(data.value)
                    })
                })
                if (vm.isStrongCategory && dataSet.weight?.length === 0) (dataSet.repsType || []).forEach(() => {
                    dataSet.weight.push(0)
                })
            }
            const len = (config && config.sets && config.sets.length) || vm.localSet
            const ensure = (key, fill = 0) => {
                if (!Array.isArray(dataSet[key])) dataSet[key] = vm.addQuantitySet(len, fill)
                else if (dataSet[key].length < len) dataSet[key].push(...vm.addQuantitySet(len - dataSet[key].length, fill))
            }
            ensure('repsType')
            if (vm.isStrongCategory) {
                ensure('rpeType')
                ensure('weight')
                ensure('tempoType')
            }
            if (vm.hasIntensity) ensure('intensityType')
            if (vm.hasMicroPause) ensure('micropause', '')
            Object.keys(DATA_SET_DEFAULTS).forEach(key => {
                if (Array.isArray(dataSet[key])) ensure(key, DATA_SET_DEFAULTS[key])
            })
            return dataSet
        },
        resetConfig() {
            const vm = this
            vm.dataSet = {}
            vm.repsType = null
            vm.rpeType = null
            vm.tempoType = null
            vm.intensityType = null
            vm.disabledWeight = false
        },
        reset() {
            const vm = this
            vm.resetConfig()
            vm.localSet = 1
            if (vm.category) {
                vm.repsType = vm.repsOptions[0]
                vm.setDataSet(1)
            }
            vm.emitValid()
        },
        emitValid() {
            this.$emit('update:valid', !!this.repsType)
        },
    },
    watch: {
        set(val) {
            if (val !== this.localSet) this.localSet = val
        },
        localSet(val, oldVal) {
            const vm = this
            vm.$emit('update:set', val)
            if (vm.populating) return
            vm.setDataSet(val, oldVal)
        },
        category(val, oldVal) {
            const vm = this
            if (oldVal == null && val == null) return
            vm.resetConfig()
            if (!val) return
            vm.repsType = vm.repsOptions[0]
            vm.localSet = 1
            vm.setDataSet(1)
        },
        maxWeight() {
            this.calculateWeight()
        },
        repsType() {
            this.emitValid()
        },
        dataSet: {
            deep: true,
            handler() {
                this.calculateWeight()
            }
        },
    },
}
</script>

<style scoped>
.sce {
    display: flex;
    gap: 8px;
}

.sce-left {
    flex-shrink: 0;
    width: 158px;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sce-spacer {
    height: 26px;
}

.sce-sel >>> .v-input__slot {
    min-height: 40px !important;
    height: 40px !important;
    margin-bottom: 0;
    border-radius: 8px;
}

.sce-sel >>> .v-input,
.sce-inp >>> .v-input {
    margin-top: 0;
    padding-top: 0;
}

.sce-sel >>> .v-text-field__details,
.sce-inp >>> .v-text-field__details {
    display: none !important;
}

.sce-sel >>> .v-input__icon--clear .v-icon {
    font-size: 18px !important;
}

.sce-static {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    height: 40px;
    padding: 0 12px;
    border-radius: 8px;
    background: var(--v-field-base);
    border: 1px solid var(--v-outline-base);
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    cursor: pointer;
}

.sce-static--plain {
    cursor: default;
}

.sce-static .v-icon {
    color: var(--v-txtMuted-base) !important;
}

.sce-static span {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.sce-scroll {
    overflow-x: auto;
    flex: 1;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 8px;
}

.sce-series {
    display: flex;
    gap: 6px;
    min-width: max-content;
}

.sce-col {
    width: 72px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.sce-shead {
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
    text-transform: uppercase;
    letter-spacing: .06em;
}

.sce-inp {
    height: 40px;
}

.sce-inp >>> .v-input__slot {
    min-height: 40px !important;
    height: 40px !important;
    margin-bottom: 0;
    border-radius: 8px;
}

.sce-inp >>> input {
    text-align: center;
}

.sce-add {
    width: 40px;
}

.sce-add-btn {
    width: 40px;
    height: 40px;
    border-radius: 8px;
    border: 1.5px dashed var(--v-outline2-base);
    background: transparent;
    color: var(--v-txtMuted-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all .15s;
}

.sce-add-btn:hover:not(:disabled) {
    border-color: var(--v-accent-base);
    color: var(--v-accent-base);
}

.sce-add-btn:disabled {
    opacity: .4;
    cursor: default;
}

.sce-add-btn .v-icon {
    color: inherit !important;
}
</style>
